export function parseContentDispositionFilename(header) {
  if (!header) return null;

  const utfMatch = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      return utfMatch[1].trim().replace(/^["']|["']$/g, "");
    }
  }

  const match = header.match(/filename="([^"]+)"/i) || header.match(/filename=([^;]+)/i);
  return match?.[1] ? match[1].trim().replace(/^["']|["']$/g, "") : null;
}

export function fallbackDownloadFilename(formType, contentType) {
  const isZip = typeof contentType === "string" && contentType.includes("zip");
  const extension = isZip ? "zip" : "pdf";
  const safeType = formType || "form";
  return `${safeType}-submission.${extension}`;
}

export function getFormSubmissionRef(submission) {
  const formType = submission?.formType || "kayak";
  const id = String(submission?._id || submission?.id || "");
  return {
    formType,
    id,
    key: `${formType}:${id}`,
  };
}

export function getDeviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

export function saveBlobFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function sanitizeFilename(name) {
  return String(name || "form-email")
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

function encodeRfc2047(text) {
  if (/^[\x20-\x7E]*$/.test(text)) {
    return text;
  }
  const bytes = encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
  return `=?UTF-8?B?${btoa(bytes)}?=`;
}

function wrapBase64(value) {
  return value.replace(/(.{76})/g, "$1\r\n");
}

async function fileToBase64(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return dataUrl.split(",")[1] || "";
}

async function buildEmlFile(file, { subject, body }) {
  const boundary = `----=_Opdox_${Date.now()}`;
  const asciiName = file.name.replace(/[^\x20-\x7E]/g, "_");
  const contentType = file.type || "application/octet-stream";
  const attachment = wrapBase64(await fileToBase64(file));
  const eml = [
    `Date: ${new Date().toUTCString()}`,
    `Subject: ${encodeRfc2047(subject)}`,
    "MIME-Version: 1.0",
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
    "",
    `--${boundary}`,
    'Content-Type: text/plain; charset="UTF-8"',
    "Content-Transfer-Encoding: 8bit",
    "",
    body.replace(/\r?\n/g, "\r\n"),
    `--${boundary}`,
    `Content-Type: ${contentType}; name="${asciiName}"`,
    "Content-Transfer-Encoding: base64",
    `Content-Disposition: attachment; filename="${asciiName}"`,
    "",
    attachment,
    `--${boundary}--`,
    "",
  ].join("\r\n");

  return new File([eml], `${sanitizeFilename(subject)}.eml`, {
    type: "message/rfc822",
  });
}

export function isNativeShareAvailable() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export async function shareFormFile(file, { title, text }) {
  if (!isNativeShareAvailable()) {
    return "unsupported";
  }

  const payloads = [
    { files: [file], title, text },
    { files: [file], title },
    { files: [file] },
  ];

  try {
    for (const payload of payloads) {
      const canShare =
        typeof navigator.canShare !== "function" || navigator.canShare(payload);
      if (!canShare) {
        continue;
      }
      await navigator.share(payload);
      return "shared";
    }
  } catch (error) {
    if (error?.name === "AbortError") {
      return "cancelled";
    }
  }

  return "unsupported";
}

export async function openEmailDraftWithAttachment(file, { subject, body }) {
  const emlFile = await buildEmlFile(file, { subject, body });
  saveBlobFile(emlFile, emlFile.name);
  return "eml";
}

export async function getDownloadErrorMessage(error) {
  const data = error?.data;
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.message || "Failed to download form submission";
    } catch {
      return "Failed to download form submission";
    }
  }
  return data?.message || error?.error || "Failed to download form submission";
}
